import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardBody, Typography } from "@material-tailwind/react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatToZMW, rootUrl } from "../../helpers";
import { useAuth } from "../../context/auth-context";
import { GrDashboard } from "react-icons/gr";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";

const Dashboard = () => {
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const auth = useAuth();

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(rootUrl(`/getCash/${auth.user.shop}`));
      setTransactions(response.data.data);
    } catch (error) {
      toast.error("Failed to load transactions.");
    }
  };

  // Calculate totals
  const totalIncome = transactions.filter(t => t.type == 1).reduce((acc, t) => acc + t.amount, 0);
  const totalExpense = transactions.filter(t => t.type == 2).reduce((acc, t) => acc + t.amount, 0);
  const balance = totalIncome - totalExpense;

  // Prepare data for charts
  const chartData = transactions.map((t, index) => ({
    name: `Txn ${index + 1}`,
    income: t.type == 1 ? t.amount : 0,
    expense: t.type == 2 ? t.amount : 0,
  }));

  let cumulativeBalance = 0;
  const balanceData = transactions.map((t, index) => {
    cumulativeBalance += t.type == 1 ? t.amount : -t.amount;
    return { name: `Txn ${index + 1}`, balance: cumulativeBalance };
  });

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <h1 className="text-3xl font-bold mb-6 text-gray-800 flex items-center">
        <GrDashboard className="mr-2" /> Dashboard
      </h1>
      <hr className="my-3" />

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        <Card className="shadow-lg bg-white">
          <CardBody>
            <Typography variant="h5" className="font-bold text-green-600">Total Income</Typography>
            <Typography variant="h3" className="text-green-600">${formatToZMW(totalIncome)}</Typography>
          </CardBody>
        </Card>
        <Card className="shadow-lg bg-white">
          <CardBody>
            <Typography variant="h5" className="font-bold text-red-600">Total Expense</Typography>
            <Typography variant="h3" className="text-red-600">${formatToZMW(totalExpense)}</Typography>
          </CardBody>
        </Card>
        <Card className="shadow-lg bg-white">
          <CardBody>
            <Typography variant="h5" className="font-bold">Balance</Typography>
            <Typography variant="h3" className="text-blue-600">${formatToZMW(balance)}</Typography>
          </CardBody>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Bar Chart: Income vs. Expenses */}
        <Card className="shadow-lg bg-white p-4">
          <Typography variant="h5" className="font-bold mb-4">Income vs. Expenses</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="income" fill="#4CAF50" name="Income" />
              <Bar dataKey="expense" fill="#F44336" name="Expense" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Line Chart: Balance Trend */}
        <Card className="shadow-lg bg-white p-4">
          <Typography variant="h5" className="font-bold mb-4">Balance Over Time</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={balanceData}>
              <XAxis dataKey="name" />
              <YAxis />
              <CartesianGrid strokeDasharray="3 3" />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="balance" stroke="#2196F3" name="Balance" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Transactions List */}
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-xl font-bold mb-4">Recent Transactions</h2>
        <div className="space-y-4">
          {transactions.map((t, index) => (
            <div key={index} className={`p-4 border-l-4 ${t.type == 1 ? "border-green-500 bg-green-50" : "border-red-500 bg-red-50"} rounded-lg`}>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-lg font-semibold">{t.refType}</p>
                  <p className="text-gray-500">{t.comment}</p>
                  <p className="text-sm text-gray-400">{new Date(t.date).toLocaleString()}</p>
                </div>
                <p className={`text-lg font-bold ${t.type == 1 ? "text-green-600" : "text-red-600"}`}>
                  ${formatToZMW(t.amount)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
